// views/admin/error/ExpirationPage.jsx
import React, { useEffect } from 'react';
import {
  Box,
  Button,
  Flex,
  Heading,
  Text,
  VStack,
  Icon,
  Divider,
} from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { FiLogOut, FiAlertTriangle, FiExternalLink } from 'react-icons/fi';
import { useModalColors } from 'hooks/useModalColors';
import { useUserSession } from 'hooks/useUserSession';
import keys from 'config/keys';

const MotionBox = motion(Box);
const MotionButton = motion(Button);

/**
 * What to tell whom, by the `error` code of the server's `forceLogout` reply
 * (subscription gate: middlewares/subscriptionMiddleware.js; account checks:
 * middlewares/tenantContextMiddleware.js).
 *
 * WHY BY CODE: this screen said "Subscription Expired" for everything, including
 * a disabled account, and only the owner can act on a subscription — staff were
 * told to "renew", which they cannot do (audit D14). So:
 *   - a subscription problem: the owner is asked to renew (and gets the link),
 *     staff are asked to tell their administrator;
 *   - an account problem: nobody is told about subscriptions.
 * Unknown codes fall back to the server's own message.
 */
const SUBSCRIPTION_SCREENS = {
  NO_SUBSCRIPTION: {
    heading: 'No Active Subscription',
    owner: 'This workspace has no active plan. Choose a plan to start using it.',
    staff: 'This workspace has no active plan yet. Please let your administrator know.',
  },
  SUBSCRIPTION_INACTIVE: {
    heading: 'Subscription Inactive',
    owner: 'Your subscription is not active. Renew it to continue using the workspace.',
    staff: "Your workspace's subscription is not active. Please let your administrator know.",
  },
  SUBSCRIPTION_EXPIRED: {
    heading: 'Subscription Expired',
    owner: 'Your subscription has ended. Renew it to continue using the workspace.',
    staff: "Your workspace's subscription has ended. Please let your administrator know.",
  },
};
const ACCOUNT_SCREENS = {
  ACCOUNT_DISABLED: {
    heading: 'Account Disabled',
    text: 'This account has been disabled. Please contact your administrator if you think this is a mistake.',
  },
  USER_NOT_FOUND: {
    heading: 'Account Not Found',
    text: 'This account no longer exists in this workspace. Please contact your administrator.',
  },
};

/** Only a web address is used as a link — never whatever else the build was given. */
const safeLink = (url) => (/^https?:\/\//i.test(String(url || '')) ? url : null);

export function screenFor(error, message, isOwner) {
  const subscription = SUBSCRIPTION_SCREENS[error];
  if (subscription) {
    return {
      label: 'Subscription',
      heading: subscription.heading,
      text: isOwner ? subscription.owner : subscription.staff,
      renew: isOwner,
    };
  }
  const account = ACCOUNT_SCREENS[error];
  if (account) return { label: 'Account', ...account, renew: false };
  return {
    label: 'Access Restricted',
    heading: 'Access Restricted',
    text: message || 'Your session has ended. Please sign in again.',
    renew: false,
  };
}

export default function ExpirationPage({ data }) {
  const colors = useModalColors();
  const { isSuperAdmin } = useUserSession();

  // Use data from props or fallback to stored data
  const errorData = data || {};

  const {
    error = 'NO_SUBSCRIPTION',
    message,
  } = errorData;

  const screen = screenFor(error, message, isSuperAdmin);
  const renewUrl = screen.renew ? safeLink(keys.productPricingUrl) : null;

  // Simple logout - just clear storage and redirect
  const handleLogout = () => {
    // Held over the wipe so the next sign-in can skip the workspace picker.
    // The /auth redirect below lands on the resolver, which re-validates both
    // before anything trusts them.
    const workspaces = localStorage.getItem('workspaces');
    const lastWorkspace = localStorage.getItem('lastWorkspace');

    // Clear all storage
    localStorage.clear();
    sessionStorage.clear();
    if (workspaces) localStorage.setItem('workspaces', workspaces);
    if (lastWorkspace) localStorage.setItem('lastWorkspace', lastWorkspace);

    // Redirect to login
    window.location.href = '/auth';
  };

  // Auto logout after 5 minutes
  useEffect(() => {
    const timer = setTimeout(() => {
      handleLogout();
    }, 5 * 60 * 1000);

    return () => clearTimeout(timer);
  }, []);

  return (
    <Flex
      minH="100vh"
      align="center"
      justify="center"
      bg={colors.bgDeep}
      px={4}
      py={8}
    >
      <MotionBox
        w="100%"
        maxW="480px"
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <Box
          bg={colors.bg}
          borderRadius="2xl"
          boxShadow={colors.modalShadow}
          overflow="hidden"
          border="1px solid"
          borderColor={colors.borderColor}
        >
          {/* Subtle Header */}
          <Box
            p={6}
            borderBottom="1px solid"
            borderColor={colors.borderColor}
            bg={colors.bgDeep}
          >
            <Flex align="center" gap={3}>
              <Box
                bg={colors.badgeWarningBg}
                p={2}
                borderRadius="lg"
                color={colors.badgeWarningText}
              >
                <Icon as={FiAlertTriangle} w={5} h={5} />
              </Box>
              <Text fontWeight="600" color={colors.headingText} fontSize="md">
                {screen.label}
              </Text>
            </Flex>
          </Box>

          <Box p={8}>
            <VStack spacing={6} align="stretch">
              {/* Icon */}
              <Flex justify="center">
                <Box
                  bg={colors.bgDeep}
                  p={6}
                  borderRadius="full"
                  border="1px solid"
                  borderColor={colors.borderColor}
                >
                  <Icon as={FiAlertTriangle} w={12} h={12} color={colors.badgeWarningText} />
                </Box>
              </Flex>

              {/* Error Details */}
              <Box textAlign="center">
                <Heading size="lg" mb={3} color={colors.headingText}>
                  {screen.heading}
                </Heading>
                <Text color={colors.bodyText} fontSize="sm" lineHeight="1.7">
                  {screen.text}
                </Text>
              </Box>

              <Divider borderColor={colors.borderColor} />

              {/* Error Info */}
              <Box>
                <Text fontSize="xs" color={colors.mutedText} textAlign="center">
                  Error Reference: <Text as="span" color={colors.accentGold} fontFamily="mono">{error}</Text>
                </Text>
              </Box>

              <Divider borderColor={colors.borderColor} />

              {/* Renew — owners only, and only when the pricing page is configured */}
              {renewUrl && (
                <Button
                  as="a"
                  href={renewUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="brand"
                  size="lg"
                  rightIcon={<FiExternalLink />}
                  py={6}
                  w="full"
                >
                  Renew subscription
                </Button>
              )}

              {/* Logout Button */}
              <MotionButton
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                variant="outline"
                borderColor={colors.borderColor}
                color={colors.bodyText}
                size="lg"
                leftIcon={<FiLogOut />}
                onClick={handleLogout}
                py={6}
                w="full"
                _hover={{
                  borderColor: colors.accentGold,
                  color: colors.accentGold,
                  bg: colors.secondaryBtnHoverBg,
                }}
                transition="all 0.2s ease"
              >
                Sign Out
              </MotionButton>
            </VStack>
          </Box>
        </Box>
      </MotionBox>
    </Flex>
  );
}
